import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { LogoMark } from '../../logo/logo-mark';
import { useAuth } from '../../../context/auth-provider';
import { confirmEmailChange } from '../../../api/profile-client';
import { resolvePostAuthRoute } from '../../../utils/post-auth-route';
import { AutoHeight, Collapse, Pop } from '../../../motion';
import {
  Shell,
  Card,
  BrandRow,
  Wordmark,
  Heading,
  Subheading,
  FullWidthButton,
  InfoBanner,
  SuccessBlock,
  SuccessIcon,
} from '../onboarding.styled';

type State = 'confirming' | 'success' | 'expired' | 'taken' | 'invalid';

/**
 * REDEEM THE LINK SENT TO A NEW EMAIL ADDRESS (migration 073).
 *
 * A separate page from /verify-email, because it does a different thing:
 * that one proves the address the account ALREADY has, this one PROMOTES a
 * pending address over the live login identifier. Two routes means the backend
 * never has to work out which of two writes a token meant from the token alone.
 *
 * Public, and it must stay public: this link lands in the NEW mailbox, which is
 * routinely opened on a phone that has never signed in to Lobbero. It is also
 * listed in SIGNED_IN_PUBLIC_PREFIXES — the person clicking it is just as often
 * the signed-in occupant, and the #15 bounce would otherwise send them to their
 * dashboard without the page ever mounting, which is exactly the bug that made
 * /verify-email impossible to complete.
 */
export const ConfirmEmailChangePage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, onboarding, permissions, refreshAuth } = useAuth();
  const token = params.get('token');
  const [state, setState] = useState<State>('confirming');
  const [newEmail, setNewEmail] = useState('');
  const ran = useRef(false); // guard React 18 StrictMode double-invoke

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState('invalid');

      return;
    }
    confirmEmailChange(token)
      .then(res => {
        setNewEmail(res.email || '');
        setState('success');

        // The signed-in session is still valid — sessions are keyed on the user
        // id, not the address — but its cached `email` is now the OLD one. This
        // is what stops the account chip from showing an address that no longer
        // signs in.
        if (user) {
          refreshAuth().catch(() => undefined);
        }
      })
      .catch(err => {
        const status = (err as AxiosError)?.response?.status;
        if (status === 410) setState('expired');
        else if (status === 409) setState('taken');
        else setState('invalid');
      });
    // Exactly once per token; the ran-guard above is what enforces it.
    // eslint-disable-next-line
  }, [token]);

  const copy = {
    confirming: {
      title: 'Confirming your new email…',
      sub: 'One moment.',
    },
    success: {
      title: 'Email address changed',
      sub: newEmail
        ? `${newEmail} is now the address you sign in with.`
        : 'This is now the address you sign in with.',
    },
    expired: {
      title: 'This link has expired',
      sub: 'Ask for the change again from Account → Details, and open the new link sooner.',
    },
    taken: {
      title: 'That address is already in use',
      sub: 'Somebody claimed it before this link was opened. Your account is unchanged and still uses its previous address.',
    },
    invalid: {
      title: 'We couldn’t confirm this link',
      sub: 'It may have already been used, or the change may have been cancelled. Your account is unchanged.',
    },
  }[state];

  return (
    <Shell>
      <Card>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero</Wordmark>
        </BrandRow>
        {/* The tick pops, the banner and the button unfold, and the card grows
            to fit — rather than all three landing in one commit. */}
        <AutoHeight>
          <Heading>{copy.title}</Heading>
          <Subheading>{copy.sub}</Subheading>
          <Collapse open={state === 'success'}>
            <SuccessBlock>
              <Pop as="div">
                <SuccessIcon aria-hidden="true">✓</SuccessIcon>
              </Pop>
            </SuccessBlock>
            <InfoBanner tone="success">
              Your previous address no longer signs in to this account.
            </InfoBanner>
          </Collapse>
          <Collapse open={state !== 'confirming'}>
            <FullWidthButton
              type="button"
              onClick={() =>
                navigate(
                  user
                    ? resolvePostAuthRoute(user, onboarding, permissions)
                    : '/'
                )
              }
            >
              {user ? 'Continue' : 'Go to sign in'}
            </FullWidthButton>
          </Collapse>
        </AutoHeight>
      </Card>
    </Shell>
  );
};
